import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 10000;

/**
 * Live-viewer badge. Polls /api/presence/live every ~10s and shows a soft
 * green pulse with the current count. Public — no auth required.
 *
 * Variants:
 *  - "hero"   : large, suitable for the homepage hero
 *  - "inline" : small chip, for navbars/admin headers
 */
const LiveViewersBadge = ({ variant = 'hero', className = '' }) => {
  // Start at 1 to count the current viewer immediately — avoids a brief "0" flash
  // before the first /api/presence/live poll resolves.
  const [count, setCount] = useState(1);

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await axios.get(`${API}/api/presence/live`);
        if (alive) setCount(data.live_count ?? 0);
      } catch (_) {
        /* keep last value */
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-300 text-xs font-medium ${className}`}
        data-testid="live-viewers-badge"
        title={`${count ?? 0} viewers right now`}
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
        </span>
        <span data-testid="live-viewers-count">{count ?? '–'}</span>
        <span className="text-green-400/70 uppercase tracking-wider text-[10px] ml-1">live</span>
      </span>
    );
  }

  // hero
  return (
    <div
      className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-black/40 border border-green-500/30 backdrop-blur ${className}`}
      data-testid="live-viewers-badge"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
      </span>
      <Eye size={14} className="text-green-300" />
        <span className="text-sm text-white">
          <span className="font-['Bebas_Neue'] text-xl tracking-wide text-green-300 mr-1.5" data-testid="live-viewers-count">
            {count ?? '…'}
          </span>
          <span className="text-[#A3A3A3] uppercase tracking-[0.18em] text-[10px]">
            {count === 1 ? 'person viewing now' : 'people viewing now'}
          </span>
        </span>
    </div>
  );
};

export default LiveViewersBadge;
